import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from '../../../../core/shared/service/crud/mvs-crud.service';
import {ExternalSyncDirectionEnum} from '../../enum/external-sync-direction.enum';
import {ExternalIntegrationTypeEnum} from '../../enum/external-integration-type.enum';

/**
 * Response interface for integration test operations.
 */
export interface IntegrationTestResponse {
    success: boolean;
    message: string;
    integrationId: number;
    integrationType: string;
    testedAt: Date;
    responseTimeMs: number;
    apiVersion?: string;
    connectionDetails?: string;
}

/**
 * Response interface for sync operations.
 */
export interface ExternalSyncResponse {
    success: boolean;
    message: string;
    executionId: number;
    campaignExternalSyncId: number;
    syncStatus: string;
    syncDirection: string;
    startedAt: Date;
    completedAt?: Date;
    durationMs?: number;
    recordsProcessed?: number;
    recordsCreated?: number;
    recordsUpdated?: number;
    recordsFailed?: number;
    errorMessage?: string;
}

/**
 * Interface for sync status summary.
 */
export interface SyncStatusSummary {
    campaignExternalSyncId: number;
    campaignId: number;
    campaignName: string;
    integrationId: number;
    integrationName: string;
    integrationType: string;
    syncDirection: string;
    syncStatus: string;
    isAutoSyncEnabled: boolean;
    lastSyncedAt?: Date;
    nextSyncAt?: Date;
    totalMembers: number;
    syncedMembers: number;
    pendingMembers: number;
    failedMembers: number;
    externalCampaignId?: string;
}

/**
 * Interface for sync execution history.
 */
export interface SyncExecution {
    id: number;
    campaignExternalSyncId: number;
    syncDirection: string;
    syncStatus: string;
    startedAt: Date;
    completedAt?: Date;
    durationMs?: number;
    recordsProcessed: number;
    recordsCreated: number;
    recordsUpdated: number;
    recordsFailed: number;
    errorMessage?: string;
    syncDetailsJson?: string;
}

/**
 * Service for External Integration functionality (F-030).
 * Provides methods for:
 * - Managing external integration configurations
 * - Testing integration connections
 * - Triggering and managing sync operations
 * - Monitoring sync status and history
 */
@Injectable({
    providedIn: 'root'
})
export class ExternalIntegrationService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/externalIntegrations');
    }

    getObjectIcon(): string {
        return 'fa-solid fa-plug';
    }

    // ===== Integration Management =====

    /**
     * Tests the connection to an external integration.
     */
    testConnection(integrationId: number): Observable<IntegrationTestResponse> {
        return this.postGeneric<IntegrationTestResponse>({}, `/${integrationId}/test`);
    }

    /**
     * Gets all active integrations.
     */
    getActiveIntegrations(): Observable<any[]> {
        return this.getGeneric<any[]>('/active');
    }

    /**
     * Gets integrations by type.
     */
    getIntegrationsByType(type: ExternalIntegrationTypeEnum): Observable<any[]> {
        const typeStr = ExternalIntegrationTypeEnum[type].toUpperCase();
        return this.getGeneric<any[]>(`/byType/${typeStr}`);
    }

    /**
     * Gets available integration types (types with configured connectors).
     */
    getAvailableIntegrationTypes(): Observable<string[]> {
        return this.getGeneric<string[]>('/availableTypes');
    }

    // ===== Campaign Sync Configuration =====

    /**
     * Configures a campaign for external sync.
     * F-030: Given integration is configured, when campaign activates, then automatic sync can be enabled.
     */
    configureCampaignSync(
        integrationId: number,
        campaignId: number,
        syncDirection: ExternalSyncDirectionEnum,
        autoSyncEnabled: boolean = false
    ): Observable<any> {
        const directionStr = ExternalSyncDirectionEnum[syncDirection].toUpperCase();
        return this.postGeneric<any>(
            {},
            `/${integrationId}/campaigns/${campaignId}/configure?syncDirection=${directionStr}&autoSyncEnabled=${autoSyncEnabled}`
        );
    }

    /**
     * Gets sync status summary for a campaign.
     */
    getCampaignSyncStatus(campaignId: number): Observable<SyncStatusSummary[]> {
        return this.getGeneric<SyncStatusSummary[]>(`/campaigns/${campaignId}/syncStatus`);
    }

    // ===== Sync Operations =====

    /**
     * Triggers a sync operation for a campaign.
     * F-030: Given campaign segment, when push to external tool is triggered, then member list is synced.
     */
    triggerSync(campaignExternalSyncId: number, fullSync: boolean = false): Observable<ExternalSyncResponse> {
        return this.postGeneric<ExternalSyncResponse>(
            {},
            `/sync/${campaignExternalSyncId}/trigger?fullSync=${fullSync}`
        );
    }

    /**
     * Enables or disables auto-sync for a campaign integration.
     */
    setAutoSync(campaignExternalSyncId: number, enabled: boolean): Observable<any> {
        return this.http.put<any>(
            `${MvsCrudService.baseUrl}/ct/externalIntegrations/sync/${campaignExternalSyncId}/autoSync?enabled=${enabled}`,
            {}
        );
    }

    /**
     * Retries failed member syncs.
     */
    retryFailedMembers(campaignExternalSyncId: number): Observable<ExternalSyncResponse> {
        return this.postGeneric<ExternalSyncResponse>({}, `/sync/${campaignExternalSyncId}/retryFailed`);
    }

    // ===== Sync History =====

    /**
     * Gets execution history for a campaign sync.
     */
    getExecutionHistory(campaignExternalSyncId: number, limit: number = 10): Observable<SyncExecution[]> {
        return this.getGeneric<SyncExecution[]>(`/sync/${campaignExternalSyncId}/history?limit=${limit}`);
    }
}
